// Módulo Categorías — 4.1: "CRUD de categorías". Alcance MVP: listado +
// alta/edición (nombre, slug, descripción, orden, estado) + baja lógica.
// SEO por categoría se edita en el mismo modal (igual patrón que
// productos.js) porque el volumen de campos SEO es pequeño y no justifica
// una pantalla separada — ver 4.5 del doc de BD sobre la tabla `seo`.

import { crearTabla, mostrarToast } from '@plataforma/ui';
import {
  esSlugValido,
  escaparHtml,
  escapeAttr,
  generarSlugSugerido,
  mensajeErrorAmigable,
} from '@plataforma/utils';

import {
  eliminarArchivo,
  rutaDesdeUrlPublica,
  subirImagenCategoria,
} from '../api/almacenamiento.js';
import {
  actualizarCategoria,
  actualizarSeoCategoria,
  crearCategoria,
  eliminarCategoria,
  listarCategorias,
  obtenerCategoria,
} from '../api/categorias.js';
import { obtenerEstadoSesion } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

export async function vistaCategorias(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/categorias');
  contenido.innerHTML = `
    <div class="pf-fila pf-fila--separada" style="margin-bottom: 16px;">
      <h1>Categorías</h1>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nueva-categoria">
        Nueva categoría
      </button>
    </div>
    <div id="pf-categorias-tabla"><p class="pf-texto-suave">Cargando…</p></div>

    <pf-modal titulo="Nueva categoría" id="pf-modal-categoria"></pf-modal>
  `;

  await cargarTabla(contenido);

  const modal = contenido.querySelector('#pf-modal-categoria');
  contenido.querySelector('#pf-btn-nueva-categoria').addEventListener('click', async () => {
    await montarFormulario(modal, null, () => cargarTabla(contenido));
    modal.setAttribute('titulo', 'Nueva categoría');
    modal.abrir();
  });
}

async function cargarTabla(contenido) {
  const destino = contenido.querySelector('#pf-categorias-tabla');
  const { data, error } = await listarCategorias();

  if (error) {
    destino.innerHTML = `<p class="pf-campo__error">No se pudieron cargar las categorías.</p>`;
    return;
  }

  const modal = contenido.querySelector('#pf-modal-categoria');

  const tabla = crearTabla({
    filaVacia: 'Todavía no hay categorías. Crea la primera con el botón de arriba.',
    columnas: [
      {
        titulo: 'Imagen',
        render: (fila) => {
          if (!fila.imagen_url) {
            const placeholder = document.createElement('span');
            placeholder.className = 'pf-texto-suave';
            placeholder.textContent = '—';
            return placeholder;
          }
          const img = document.createElement('img');
          img.src = fila.imagen_url;
          img.alt = '';
          img.loading = 'lazy';
          img.style.cssText =
            'width:52px;height:40px;object-fit:cover;border-radius:8px;display:block;';
          return img;
        },
      },
      { clave: 'nombre', titulo: 'Nombre' },
      { clave: 'slug', titulo: 'Slug' },
      { clave: 'orden', titulo: 'Orden' },
      {
        titulo: 'Estado',
        render: (fila) => {
          const badge = document.createElement('pf-badge-estado');
          badge.setAttribute('tono', fila.estado === 'visible' ? 'exito' : 'neutral');
          badge.textContent = fila.estado === 'visible' ? 'Visible' : 'Oculta';
          return badge;
        },
      },
      {
        titulo: 'Acciones',
        render: (fila) => crearAccionesFila(fila, modal, () => cargarTabla(contenido)),
      },
    ],
    filas: data ?? [],
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);
}

function crearAccionesFila(categoria, modal, alTerminar) {
  const contenedor = document.createElement('div');
  contenedor.className = 'pf-fila';

  const botonEditar = document.createElement('button');
  botonEditar.type = 'button';
  botonEditar.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  botonEditar.textContent = 'Editar';
  botonEditar.addEventListener('click', async () => {
    await montarFormulario(modal, categoria.id, alTerminar);
    modal.setAttribute('titulo', `Editar: ${categoria.nombre}`);
    modal.abrir();
  });

  const botonEliminar = document.createElement('button');
  botonEliminar.type = 'button';
  botonEliminar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
  botonEliminar.textContent = 'Eliminar';
  botonEliminar.addEventListener('click', async () => {
    if (
      !window.confirm(
        `¿Eliminar "${categoria.nombre}"? Los productos que dependan de esta categoría como principal se reasignan automáticamente a otra, o quedan sin categoría si no hay ninguna más.`,
      )
    ) {
      return;
    }
    botonEliminar.disabled = true;
    const { error } = await eliminarCategoria(categoria.id);
    if (error) {
      mostrarToast('No se pudo eliminar la categoría.', { tono: 'peligro' });
      botonEliminar.disabled = false;
      return;
    }
    mostrarToast('Categoría eliminada.', { tono: 'exito' });
    await alTerminar();
  });

  contenedor.append(botonEditar, botonEliminar);
  return contenedor;
}

/**
 * @param {HTMLElement} modal
 * @param {string | null} categoriaId - null para alta
 * @param {() => Promise<void>} alGuardar
 */
async function montarFormulario(modal, categoriaId, alGuardar) {
  let categoria = null;
  if (categoriaId) {
    const { data, error } = await obtenerCategoria(categoriaId);
    if (error) {
      mostrarToast('No se pudo cargar la categoría.', { tono: 'peligro' });
      return;
    }
    categoria = data;
  }

  modal.innerHTML = `
    <form id="pf-form-categoria" novalidate>
      <div class="pf-campo">
        <label for="pf-cat-nombre">Nombre</label>
        <input type="text" id="pf-cat-nombre" required value="${escapeAttr(categoria?.nombre ?? '')}" />
      </div>

      <div class="pf-campo">
        <label for="pf-cat-slug">Slug</label>
        <input type="text" id="pf-cat-slug" required value="${escapeAttr(categoria?.slug ?? '')}" />
        <small>Usado en la URL de la categoría en tu tienda.</small>
      </div>

      <div class="pf-campo">
        <label for="pf-cat-descripcion">Descripción</label>
        <textarea id="pf-cat-descripcion" rows="3">${escaparHtml(categoria?.descripcion ?? '')}</textarea>
      </div>

      <div class="pf-campo">
        <label for="pf-cat-orden">Orden</label>
        <input type="number" id="pf-cat-orden" min="0" value="${categoria?.orden ?? 0}" />
      </div>

      <div class="pf-campo">
        <label for="pf-cat-estado">Visibilidad</label>
        <select id="pf-cat-estado">
          <option value="visible" ${categoria?.estado === 'visible' || !categoria ? 'selected' : ''}>Visible en la tienda</option>
          <option value="oculta" ${categoria?.estado === 'oculta' ? 'selected' : ''}>Oculta</option>
        </select>
      </div>

      <hr style="border: none; border-top: 1px solid var(--pf-color-borde); margin: 16px 0;" />
      <p class="pf-texto-suave" style="margin: 0 0 8px;">Imagen de portada (opcional)</p>

      <div class="pf-campo">
        <img id="pf-cat-imagen-prevista" alt="" style="width:100%;max-height:160px;object-fit:cover;border-radius:12px;border:1px solid var(--pf-color-borde);display:${categoria?.imagen_url ? 'block' : 'none'};margin-bottom:8px;" />
        <label for="pf-cat-imagen-input">Elegir imagen</label>
        <input type="file" id="pf-cat-imagen-input" accept="image/jpeg,image/png,image/webp" />
        <small>Se muestra como tarjeta destacada en la portada de tu tienda. JPG, PNG o WEBP.</small>
        <button type="button" class="pf-boton pf-boton--peligro pf-boton--chico" id="pf-cat-imagen-quitar" style="margin-top:8px;display:${categoria?.imagen_url ? 'inline-flex' : 'none'};">
          Quitar imagen
        </button>
      </div>

      <hr style="border: none; border-top: 1px solid var(--pf-color-borde); margin: 16px 0;" />
      <p class="pf-texto-suave" style="margin: 0 0 8px;">SEO (opcional)</p>

      <div class="pf-campo">
        <label for="pf-cat-seo-titulo">Meta título</label>
        <input type="text" id="pf-cat-seo-titulo" value="${escapeAttr(categoria?.seo?.meta_titulo ?? '')}" />
      </div>

      <div class="pf-campo">
        <label for="pf-cat-seo-descripcion">Meta descripción</label>
        <textarea id="pf-cat-seo-descripcion" rows="2">${escaparHtml(categoria?.seo?.meta_descripcion ?? '')}</textarea>
      </div>

      <p class="pf-campo__error" id="pf-cat-error" hidden></p>

      <div class="pf-fila" style="justify-content: flex-end;">
        <button type="submit" class="pf-boton pf-boton--primario" id="pf-cat-submit">
          ${categoria ? 'Guardar cambios' : 'Crear categoría'}
        </button>
      </div>
    </form>
  `;

  const form = modal.querySelector('#pf-form-categoria');
  const campoNombre = modal.querySelector('#pf-cat-nombre');
  const campoSlug = modal.querySelector('#pf-cat-slug');
  const errorEl = modal.querySelector('#pf-cat-error');
  const submitEl = modal.querySelector('#pf-cat-submit');

  // Imagen de portada: `imagenNueva` es el archivo recién elegido (aún sin
  // subir), `imagenActual` la URL persistida, `imagenBorrada` si el usuario
  // pidió quitarla. La subida real ocurre recién al guardar, así no hay
  // huérfanos en Storage si el usuario cierra el modal.
  const inputImagen = modal.querySelector('#pf-cat-imagen-input');
  const previstaImg = modal.querySelector('#pf-cat-imagen-prevista');
  const quitarImagenBtn = modal.querySelector('#pf-cat-imagen-quitar');
  let imagenNueva = null;
  const imagenActual = categoria?.imagen_url ?? null;
  let imagenBorrada = false;

  function pintarPrevista() {
    if (imagenNueva) {
      previstaImg.src = URL.createObjectURL(imagenNueva);
      previstaImg.style.display = 'block';
      quitarImagenBtn.style.display = 'inline-flex';
    } else if (imagenBorrada || !imagenActual) {
      previstaImg.removeAttribute('src');
      previstaImg.style.display = 'none';
      quitarImagenBtn.style.display = 'none';
    } else {
      previstaImg.src = imagenActual;
      previstaImg.style.display = 'block';
      quitarImagenBtn.style.display = 'inline-flex';
    }
  }

  inputImagen.addEventListener('change', () => {
    const archivo = inputImagen.files?.[0];
    if (archivo) {
      imagenNueva = archivo;
      imagenBorrada = false;
      pintarPrevista();
    }
  });

  quitarImagenBtn.addEventListener('click', () => {
    imagenNueva = null;
    imagenBorrada = true;
    inputImagen.value = '';
    pintarPrevista();
  });

  pintarPrevista();

  let slugTocadoManualmente = Boolean(categoria);
  campoSlug.addEventListener('input', () => (slugTocadoManualmente = true));
  campoNombre.addEventListener('input', () => {
    if (!slugTocadoManualmente) campoSlug.value = generarSlugSugerido(campoNombre.value);
  });

  form.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    errorEl.hidden = true;

    const datos = {
      nombre: campoNombre.value.trim(),
      slug: campoSlug.value.trim().toLowerCase(),
      descripcion: modal.querySelector('#pf-cat-descripcion').value.trim() || null,
      orden: Math.max(0, Number(modal.querySelector('#pf-cat-orden').value) || 0),
      estado: modal.querySelector('#pf-cat-estado').value,
    };

    const errores = [];
    if (datos.nombre.length < 2) errores.push('El nombre es obligatorio.');
    if (!esSlugValido(datos.slug)) errores.push('El slug no tiene un formato válido.');

    if (errores.length > 0) {
      errorEl.textContent = errores.join(' ');
      errorEl.hidden = false;
      return;
    }

    submitEl.disabled = true;
    submitEl.textContent = 'Guardando…';

    // Resolver la imagen de portada antes de persistir la categoría.
    if (imagenNueva) {
      const { empresa_id: empresaId } = obtenerEstadoSesion().claims;
      const { url, error: errorImagen } = await subirImagenCategoria(empresaId, imagenNueva);
      if (errorImagen) {
        mostrarToast(errorImagen.message ?? 'No se pudo subir la imagen.', { tono: 'peligro' });
        submitEl.disabled = false;
        submitEl.textContent = categoria ? 'Guardar cambios' : 'Crear categoría';
        return;
      }
      datos.imagen_url = url;
    } else if (imagenBorrada) {
      datos.imagen_url = null;
    }

    const datosSeo = {
      meta_titulo: modal.querySelector('#pf-cat-seo-titulo').value.trim() || null,
      meta_descripcion: modal.querySelector('#pf-cat-seo-descripcion').value.trim() || null,
    };

    const { data: guardada, error } = categoria
      ? await actualizarCategoria(categoria.id, datos)
      : await crearCategoria(datos);

    if (error) {
      errorEl.textContent = mensajeErrorAmigable(error, 'No se pudo guardar la categoría.');
      errorEl.hidden = false;
      submitEl.disabled = false;
      submitEl.textContent = categoria ? 'Guardar cambios' : 'Crear categoría';
      return;
    }

    // Recuerda limpiar el archivo viejo en Storage cuando se reemplazó o
    // quitó, para no dejar huérfanos (best-effort: si falla no es crítico).
    if (imagenActual && (imagenNueva || imagenBorrada)) {
      const rutaVieja = rutaDesdeUrlPublica(imagenActual);
      if (rutaVieja) await eliminarArchivo(rutaVieja);
    }

    const seoId = categoria?.seo_id ?? guardada.seo_id;
    if (seoId) await actualizarSeoCategoria(seoId, datosSeo);

    modal.cerrar();
    mostrarToast(categoria ? 'Categoría actualizada.' : 'Categoría creada.', { tono: 'exito' });
    await alGuardar();
  });
}
